import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Calendar, Plus, Clock, CheckCircle, Phone, Mail, MessageCircle,
  MapPin, Users, Home, AlertCircle, ChevronLeft, Loader2,
  ChevronRight, Filter, Bell, ListTodo, FileText
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const ACTIVITY_TYPES = {
  llamada: { name: 'Llamada', icon: Phone, color: 'bg-blue-100 text-blue-700' },
  email: { name: 'Email', icon: Mail, color: 'bg-green-100 text-green-700' },
  whatsapp: { name: 'WhatsApp', icon: MessageCircle, color: 'bg-emerald-100 text-emerald-700' },
  reunion: { name: 'Reunión', icon: Users, color: 'bg-purple-100 text-purple-700' },
  visita: { name: 'Visita', icon: MapPin, color: 'bg-amber-100 text-amber-700' },
  tarea: { name: 'Tarea', icon: ListTodo, color: 'bg-indigo-100 text-indigo-700' },
  nota: { name: 'Nota', icon: FileText, color: 'bg-gray-100 text-gray-700' },
  documento: { name: 'Documento', icon: FileText, color: 'bg-rose-100 text-rose-700' },
};

const PRIORITIES = {
  low: { name: 'Baja', color: 'bg-gray-100 text-gray-600' },
  normal: { name: 'Normal', color: 'bg-blue-100 text-blue-700' },
  high: { name: 'Alta', color: 'bg-amber-100 text-amber-700' },
  urgent: { name: 'Urgente', color: 'bg-red-100 text-red-700' },
};

export default function CRMActivitiesPage() {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('pending');
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [overdueCount, setOverdueCount] = useState(0);
  const [todayCount, setTodayCount] = useState(0);
  
  const [formData, setFormData] = useState({
    subject: '',
    activity_type: 'llamada',
    contact_id: '',
    opportunity_id: '',
    property_id: '',
    due_date: '',
    due_time: '',
    priority: 'normal',
    description: '',
    location: ''
  });

  const token = localStorage.getItem('upim_token');
  const navigate = useNavigate();
  const limit = 20;

  useEffect(() => {
    loadData();
  }, [page, filterType, filterStatus]);

  const loadData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('limit', limit);
      params.append('offset', (page - 1) * limit);
      if (filterType) params.append('activity_type', filterType);
      if (filterStatus === 'pending') {
        params.append('status', 'pendiente');
      } else if (filterStatus === 'overdue') {
        params.append('overdue', 'true');
      } else if (filterStatus === 'today') {
        params.append('due_today', 'true');
      } else if (filterStatus === 'completed') {
        params.append('status', 'completada');
      }

      const [activitiesRes, contactsRes, overdueRes, todayRes] = await Promise.all([
        axios.get(`${API_URL}/api/crm/activities?${params}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/crm/contacts?limit=100`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/crm/activities?overdue=true&limit=1`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/crm/activities?due_today=true&limit=1`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setActivities(activitiesRes.data.activities || []);
      setTotal(activitiesRes.data.total || 0);
      setContacts(contactsRes.data.contacts || []);
      setOverdueCount(overdueRes.data.total || 0);
      setTodayCount(todayRes.data.total || 0);
    } catch (err) {
      console.error('Error loading activities:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    setFormData({
      subject: '',
      activity_type: 'llamada',
      contact_id: '',
      opportunity_id: '',
      property_id: '',
      due_date: tomorrow.toISOString().split('T')[0],
      due_time: '10:00',
      priority: 'normal',
      description: '',
      location: ''
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      let due_date = null;
      if (formData.due_date) {
        due_date = new Date(`${formData.due_date}T${formData.due_time || '09:00'}:00`).toISOString();
      }

      const payload = {
        subject: formData.subject,
        activity_type: formData.activity_type,
        contact_id: formData.contact_id || null,
        opportunity_id: formData.opportunity_id || null,
        property_id: formData.property_id || null,
        due_date,
        priority: formData.priority,
        description: formData.description || null,
        location: formData.location || null,
      };

      await axios.post(`${API_URL}/api/crm/activities`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setShowModal(false);
      loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al crear actividad');
    } finally {
      setSaving(false);
    }
  };

  const completeActivity = async (activityId) => {
    try {
      await axios.patch(`${API_URL}/api/crm/activities/${activityId}/complete`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadData();
    } catch (err) {
      alert('Error al completar actividad');
    }
  };

  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date();
  };

  const isToday = (dueDate) => {
    if (!dueDate) return false;
    const today = new Date();
    const due = new Date(dueDate);
    return due.toDateString() === today.toDateString();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    if (date.toDateString() === today.toDateString()) {
      return `Hoy, ${date.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}`;
    }
    if (date.toDateString() === tomorrow.toDateString()) {
      return `Mañana, ${date.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}`;
    }
    return date.toLocaleDateString('es-MX', { 
      weekday: 'short', 
      day: 'numeric', 
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-500 to-orange-600 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="flex items-center gap-4">
                <Button 
                  variant="ghost" 
                  size="sm"
                  className="text-white hover:bg-white/10"
                  onClick={() => navigate('/crm')}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  CRM
                </Button>
                <div>
                  <h1 className="text-2xl lg:text-3xl font-bold flex items-center gap-3">
                    <Calendar className="h-8 w-8" />
                    Actividades
                  </h1>
                  <p className="text-amber-100 mt-1">Gestiona tus tareas, llamadas y seguimientos</p>
                </div>
              </div>
              <Button 
                className="bg-white text-amber-600 hover:bg-amber-50"
                onClick={handleCreate}
              >
                <Plus className="h-4 w-4 mr-2" />
                Nueva Actividad
              </Button>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Alert Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Card 
              className={`cursor-pointer transition-all ${filterStatus === 'overdue' ? 'ring-2 ring-red-500' : ''}`}
              onClick={() => { setFilterStatus('overdue'); setPage(1); }}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-red-100 flex items-center justify-center">
                    <AlertCircle className="h-5 w-5 text-red-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-red-600">{overdueCount}</p>
                    <p className="text-sm text-gray-500">Vencidas</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card 
              className={`cursor-pointer transition-all ${filterStatus === 'today' ? 'ring-2 ring-amber-500' : ''}`}
              onClick={() => { setFilterStatus('today'); setPage(1); }}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-amber-100 flex items-center justify-center">
                    <Bell className="h-5 w-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-amber-600">{todayCount}</p>
                    <p className="text-sm text-gray-500">Para hoy</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card 
              className={`cursor-pointer transition-all ${filterStatus === 'pending' ? 'ring-2 ring-blue-500' : ''}`}
              onClick={() => { setFilterStatus('pending'); setPage(1); }}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                    <Clock className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-blue-600">{total}</p>
                    <p className="text-sm text-gray-500">Pendientes</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card 
              className={`cursor-pointer transition-all ${filterStatus === 'completed' ? 'ring-2 ring-green-500' : ''}`}
              onClick={() => { setFilterStatus('completed'); setPage(1); }}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-green-100 flex items-center justify-center">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-green-600">-</p>
                    <p className="text-sm text-gray-500">Completadas</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Filters */}
          <Card className="mb-6">
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-2">
                <Button
                  variant={filterType === '' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => { setFilterType(''); setPage(1); }}
                >
                  Todos
                </Button>
                {Object.entries(ACTIVITY_TYPES).map(([key, val]) => {
                  const Icon = val.icon;
                  return (
                    <Button
                      key={key}
                      variant={filterType === key ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => { setFilterType(key); setPage(1); }}
                    >
                      <Icon className="h-4 w-4 mr-1" />
                      {val.name}
                    </Button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Activities List */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : activities.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Calendar className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">Sin actividades</h3>
                <p className="text-gray-500 mb-6">No tienes actividades pendientes</p>
                <Button onClick={handleCreate}>
                  <Plus className="h-4 w-4 mr-2" />
                  Crear Actividad
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {activities.map(activity => {
                const typeConfig = ACTIVITY_TYPES[activity.activity_type] || ACTIVITY_TYPES.tarea;
                const priorityConfig = PRIORITIES[activity.priority] || PRIORITIES.normal;
                const TypeIcon = typeConfig.icon;
                const overdue = !activity.is_completed && isOverdue(activity.due_date);
                const today = isToday(activity.due_date);
                
                return (
                  <Card 
                    key={activity.id} 
                    className={`transition-all ${
                      activity.is_completed ? 'opacity-60' : 
                      overdue ? 'border-red-300 bg-red-50/50' :
                      today ? 'border-amber-300 bg-amber-50/50' : ''
                    }`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                            activity.is_completed ? 'bg-green-100' : typeConfig.color.split(' ')[0]
                          }`}>
                            {activity.is_completed ? (
                              <CheckCircle className="h-5 w-5 text-green-600" />
                            ) : (
                              <TypeIcon className={`h-5 w-5 ${typeConfig.color.split(' ')[1]}`} />
                            )}
                          </div>
                          <div>
                            <h3 className={`font-semibold ${activity.is_completed ? 'line-through text-gray-500' : 'text-gray-900'}`}>
                              {activity.subject}
                            </h3>
                            <div className="flex items-center gap-3 mt-1 text-sm">
                              <span className={`flex items-center gap-1 ${
                                overdue ? 'text-red-600 font-medium' : 
                                today ? 'text-amber-600 font-medium' : 'text-gray-500'
                              }`}>
                                <Clock className="h-3 w-3" />
                                {formatDate(activity.due_date)}
                              </span>
                              {activity.contact_id && (
                                <span className="flex items-center gap-1 text-gray-500">
                                  <Users className="h-3 w-3" />
                                  Contacto vinculado
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-3">
                          <Badge className={typeConfig.color}>
                            {typeConfig.name}
                          </Badge>
                          <Badge className={priorityConfig.color}>
                            {priorityConfig.name}
                          </Badge>
                          {!activity.is_completed && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => completeActivity(activity.id)}
                              className="text-green-600 hover:bg-green-50"
                            >
                              <CheckCircle className="h-4 w-4 mr-1" />
                              Completar
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between pt-4">
                  <p className="text-sm text-gray-500">
                    Mostrando {(page - 1) * limit + 1} - {Math.min(page * limit, total)} de {total}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page === 1}
                      onClick={() => setPage(p => p - 1)}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="px-4 py-2 text-sm">
                      Página {page} de {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page === totalPages}
                      onClick={() => setPage(p => p + 1)}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Create Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b sticky top-0 bg-white">
              <h2 className="text-xl font-bold text-gray-900">Nueva Actividad</h2>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Asunto *</label>
                <Input
                  value={formData.subject}
                  onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                  placeholder="Ej: Llamar a cliente García"
                  required
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de actividad</label>
                  <select
                    value={formData.activity_type}
                    onChange={(e) => setFormData(prev => ({ ...prev, activity_type: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                  >
                    {Object.entries(ACTIVITY_TYPES).map(([key, val]) => (
                      <option key={key} value={key}>{val.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Prioridad</label>
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                  >
                    {Object.entries(PRIORITIES).map(([key, val]) => (
                      <option key={key} value={key}>{val.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Fecha</label>
                  <Input
                    type="date"
                    value={formData.due_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, due_date: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Hora</label>
                  <Input
                    type="time"
                    value={formData.due_time}
                    onChange={(e) => setFormData(prev => ({ ...prev, due_time: e.target.value }))}
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contacto relacionado</label>
                <select
                  value={formData.contact_id}
                  onChange={(e) => setFormData(prev => ({ ...prev, contact_id: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                >
                  <option value="">Sin contacto asignado</option>
                  {contacts.map(c => (
                    <option key={c.id} value={c.id}>{c.full_name}</option>
                  ))}
                </select>
              </div>
              
              {(formData.activity_type === 'reunion' || formData.activity_type === 'visita') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ubicación</label>
                  <Input
                    value={formData.location}
                    onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                    placeholder="Dirección o lugar de la reunión"
                  />
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                  placeholder="Detalles adicionales..."
                />
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" className="flex-1" onClick={() => setShowModal(false)}>
                  Cancelar
                </Button>
                <Button type="submit" className="flex-1 bg-amber-600 hover:bg-amber-700" disabled={saving}>
                  {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Crear Actividad
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
